import { Check, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Modal,
  Typography,
} from '@mui/material';
import { useEntityActions } from 'api/entity';
import { useNotifications } from 'components/notify';
import { useClipboard } from 'providers/ClipboardProvider';
import React, { useState } from 'react';
import { FinsightEntity } from 'shared';

interface MoveEntitiesModalProps {
  open: boolean;
  onClose: () => void;
  targetEntity: FinsightEntity;
  onConfirm: () => void;
}

const MoveEntitiesModal: React.FC<MoveEntitiesModalProps> = ({
  open,
  onClose,
  targetEntity,
  onConfirm,
}) => {
  const { copiedEntities, clearClipboard } = useClipboard();
  const { updateEntity } = useEntityActions();
  const { showNotification } = useNotifications();
  const [isMoving, setIsMoving] = useState(false);

  // Filter out the target entity from the clipboard
  const entitiesToMove = copiedEntities.filter(
    (entity) => entity.id !== targetEntity.id,
  );

  const handleConfirm = async () => {
    if (entitiesToMove.length === 0) {
      onClose();
      return;
    }

    setIsMoving(true);
    showNotification('Moving entities...', { loading: true });

    try {
      // Move each entity by updating its parent to the target entity
      const movePromises = entitiesToMove.map(async (entity) => {
        const response = await updateEntity(
          entity.id,
          {
            parent: targetEntity.id,
          },
          entity,
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Failed to move ${entity.name}`);
        }

        return response;
      });

      await Promise.all(movePromises);

      // Clear the clipboard after successful move
      clearClipboard();

      showNotification(
        `Successfully moved ${entitiesToMove.length} entities into "${targetEntity.name}"`,
        { variant: 'success' },
      );

      onConfirm();
      onClose();
    } catch (error) {
      console.error('Error moving entities:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to move entities. Please try again.';
      showNotification(errorMessage, {
        variant: 'error',
      });
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Card
        sx={{
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: 600,
        }}
      >
        <CardHeader
          title={`Move ${entitiesToMove.length} entities into "${targetEntity.name}"`}
          action={
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          }
        />
        <CardContent
          sx={{
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
          }}
        >
          {entitiesToMove.length === 0 ? (
            <Typography color="text.secondary">
              No entities in clipboard to move.
            </Typography>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                The following entities will be moved into &quot;
                {targetEntity.name}&quot;:
              </Typography>
              <List dense>
                {entitiesToMove.map((entity) => (
                  <ListItem key={entity.id} sx={{ px: 0 }}>
                    <ListItemText
                      primary={entity.name}
                      secondary={entity.types[0]}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
        <CardActions
          sx={{
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <Button onClick={onClose} disabled={isMoving}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            startIcon={<Check />}
            disabled={entitiesToMove.length === 0 || isMoving}
          >
            {isMoving ? 'Moving...' : `Confirm Move (${entitiesToMove.length})`}
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
};

export default MoveEntitiesModal;
